import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // 🆕 Importar useNavigate
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import Sidebar from '../../components/empsidebar/empsidebar';
import Navbar from '../../components/empnavbar/empnavbar';
import { API_URLS } from '../../Services/servicioURL';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride'; 

// Interfaces
interface Empleado {
    cveemp: string;
    nomemp: string;
}
interface Sucursal {
    id: number;
    nombre: string;
}
interface Departamento {
    cve: string;
    nombre: string;
}
interface PeticionType {
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    fechapromesa?: string | null;
    motivo?: string | null;
    imagen_url?: string | null;
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

// Funciones para obtener datos
const obtenerEmpleados = async () => {
    try {
        const response = await fetch(API_URLS.empleados.obtener);
        if (!response.ok) throw new Error('Error al obtener los empleados');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};
const obtenerSucursales = async () => {
    try {
        const response = await fetch(API_URLS.sucursales.obtener);
        if (!response.ok) throw new Error('Error al obtener las sucursales');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};
const obtenerDepartamentos = async () => {
    try {
        const response = await fetch(API_URLS.departamentos.obtener);
        if (!response.ok) throw new Error('Error al obtener los departamentos');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};
const obtenerSiguienteFolio = async () => {
    try {
        const response = await fetch(API_URLS.peticiones.obtener);
        if (!response.ok) throw new Error('Error al obtener las peticiones');
        const peticiones: PeticionType[] = await response.json();
        if (peticiones.length === 0) {
            return 1;
        }
        const folioMaximo = Math.max(...peticiones.map(p => p.folio));
        return folioMaximo + 1;
    } catch (error) {
        console.error('Error al obtener siguiente folio:', error);
        return 1;
    }
};

const Peticiones: React.FC = () => {
    const navigate = useNavigate(); // 🆕 Hook para navegación
    const [folio, setFolio] = useState('');
    const [empleado, setEmpleado] = useState<Empleado | null>(null);
    const [titulo, setTitulo] = useState('');
    const [justificacion, setJustificacion] = useState('');
    const [sucursal, setSucursal] = useState<Sucursal | null>(null);
    const [departamento, setDepartamento] = useState<Departamento | null>(null);
    const [empleados, setEmpleados] = useState<Empleado[]>([]);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [mode, setMode] = useState<'view' | 'new'>('new');
    const toast = useRef<Toast>(null);
    const [mostrarTablaConsultaPeticiones, setMostrarTablaConsultaPeticiones] = useState<boolean>(false);
    const [peticionesConsulta, setPeticionesConsulta] = useState<PeticionType[]>([]);
    const [filteredPeticiones, setFilteredPeticiones] = useState<PeticionType[]>([]);
    const [searchTermPeticion, setSearchTermPeticion] = useState<string>('');
    const [imagenSeleccionada, setImagenSeleccionada] = useState<File | null>(null);
    const [previewImagen, setPreviewImagen] = useState<string | null>(null);
    const [mostrarCamara, setMostrarCamara] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [streamCamara, setStreamCamara] = useState<MediaStream | null>(null);

    // 👉 Estados para el tour guiado
    const [runTour, setRunTour] = useState<boolean>(false);
    const [tourCompleted, setTourCompleted] = useState<boolean>(false);

    // 👉 Definición de los pasos del tour
    const tourSteps: Step[] = [
        {
            target: 'body',
            content: (
                <div>
                    <h4>👋 Bienvenido al Módulo de Peticiones</h4>
                    <p>Haz clic en "Siguiente" para conocer cómo registrar y consultar tus peticiones.</p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '[data-tour="folio"]',
            content: (
                <div>
                    <h4>📄 Folio Automático</h4>
                    <p>Este número único se genera automáticamente. No es editable.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '[data-tour="empleado"]',
            content: (
                <div>
                    <h4>👤 Empleado Loggeado</h4>
                    <p>El sistema reconoce automáticamente tu nombre. No es necesario seleccionarlo.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '[data-tour="sucursal"]',
            content: (
                <div>
                    <h4>🏢 Sucursal</h4>
                    <p>Selecciona la sucursal relacionada con tu solicitud.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '[data-tour="departamento"]',
            content: (
                <div>
                    <h4>🏬 Departamento</h4>
                    <p>Elige el departamento al que pertenece tu petición.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '[data-tour="titulo"]',
            content: (
                <div>
                    <h4>📝 Título</h4>
                    <p>Ingresa un título breve y claro que describa tu petición.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '[data-tour="justificacion"]',
            content: (
                <div>
                    <h4>📋 Justificación</h4>
                    <p>Describe con detalle el motivo de tu petición. Mientras más clara, mejor.</p>
                </div>
            ),
            placement: 'top',
        },
        {
            target: '[data-tour="imagen"]',
            content: (
                <div>
                    <h4>📷 Adjuntar Imagen (Opcional)</h4>
                    <p>Puedes subir una imagen o tomar una foto directamente desde tu cámara.</p>
                </div>
            ),
            placement: 'top',
        },
        {
            target: '[data-tour="acciones"]',
            content: (
                <div>
                    <h4>✅ Enviar o Limpiar</h4>
                    <p>Usa estos botones para enviar tu petición o limpiar el formulario.</p>
                </div>
            ),
            placement: 'top',
        },
        {
            target: '[data-tour="ver-peticiones"]',
            content: (
                <div>
                    <h4>🏠 Ir al Inicio</h4>
                    <p>Haz clic aquí para ir a la pantalla de inicio donde puedes ver todas tus peticiones.</p>
                </div>
            ),
            placement: 'left',
        },
    ];

    // 👉 Callback al finalizar el tour
    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status } = data;
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunTour(false);
            setTourCompleted(true);
            localStorage.setItem('tourCompleted_peticiones', 'true');
        }
    };

    // 🆕 Función para navegar al inicio
    const handleIrAlInicio = () => {
        navigate('/empinicio');
    };

    // Función para establecer empleado loggeado
    const establecerEmpleadoLoggeado = () => {
        const cveEmpleadoLoggeado = localStorage.getItem('cveEmpleado');
        const nombreEmpleadoLoggeado = localStorage.getItem('nombreEmpleado');
        if (cveEmpleadoLoggeado && nombreEmpleadoLoggeado) {
            const empleadoLoggeado = { cveemp: cveEmpleadoLoggeado, nomemp: nombreEmpleadoLoggeado };
            setEmpleado(empleadoLoggeado);
            return empleadoLoggeado;
        }
        return null;
    };

    // Función para generar y establecer el siguiente folio
    const generarSiguienteFolio = async () => {
        try {
            const siguienteFolio = await obtenerSiguienteFolio();
            setFolio(siguienteFolio.toString());
        } catch (error) {
            console.error('Error al generar folio:', error);
            setFolio('1');
        }
    };

    // Funciones para manejo de imágenes
    const handleSeleccionarArchivo = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
            if (!tiposPermitidos.includes(file.type)) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Archivo no válido',
                    detail: 'Solo se permiten archivos de imagen (JPG, PNG, GIF, BMP, WEBP)',
                    life: 3000
                });
                return;
            }
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Archivo muy grande',
                    detail: 'El archivo no puede ser mayor a 10MB',
                    life: 3000
                });
                return;
            }
            setImagenSeleccionada(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImagen(e.target?.result as string);
            };
            reader.readAsDataURL(file);
            toast.current?.show({
                severity: 'success',
                summary: 'Imagen seleccionada',
                detail: `Archivo: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
                life: 3000
            });
        }
    };

    const iniciarCamara = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            setStreamCamara(stream);
            setMostrarCamara(true);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Error al acceder a la cámara:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error de cámara',
                detail: 'No se pudo acceder a la cámara. Verifique los permisos.',
                life: 3000
            });
        }
    };

    const tomarFoto = () => {
        if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(video, 0, 0);
                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], `foto_peticion_${Date.now()}.jpg`, { type: 'image/jpeg' });
                        setImagenSeleccionada(file);
                        setPreviewImagen(canvas.toDataURL());
                        cerrarCamara();
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Foto capturada',
                            detail: 'La foto se ha capturado correctamente',
                            life: 3000
                        });
                    }
                }, 'image/jpeg', 0.8);
            }
        }
    };

    const cerrarCamara = () => {
        if (streamCamara) {
            streamCamara.getTracks().forEach(track => track.stop());
            setStreamCamara(null);
        }
        setMostrarCamara(false);
    };

    const eliminarImagen = () => {
        setImagenSeleccionada(null);
        setPreviewImagen(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toast.current?.show({
            severity: 'info',
            summary: 'Imagen eliminada',
            detail: 'La imagen ha sido eliminada del formulario',
            life: 2000
        });
    };

    // Cargar datos iniciales
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            establecerEmpleadoLoggeado();
            await generarSiguienteFolio();
            const [empleadosData, sucursalesData, departamentosData] = await Promise.all([
                obtenerEmpleados(),
                obtenerSucursales(),
                obtenerDepartamentos()
            ]);
            setEmpleados(empleadosData);
            setSucursales(sucursalesData);
            setDepartamentos(departamentosData);
            setLoading(false);
        };
        fetchData();
    }, []);

    // Limpiar stream de cámara al desmontar
    useEffect(() => {
        return () => {
            if (streamCamara) {
                streamCamara.getTracks().forEach(track => track.stop());
            }
        };
    }, [streamCamara]);

    // Obtener peticiones para consulta
    const obtenerPeticiones = async () => {
        try {
            const cveEmpleadoLoggeado = localStorage.getItem('cveEmpleado');
            if (!cveEmpleadoLoggeado) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Advertencia',
                    detail: 'No se encontró información del empleado loggeado',
                    life: 3000
                });
                return;
            }
            const response = await fetch(API_URLS.peticiones.obtenerPorEmpleado(cveEmpleadoLoggeado));
            if (!response.ok) throw new Error('Error al obtener las peticiones');
            const data: PeticionType[] = await response.json();
            setPeticionesConsulta(data);
            setFilteredPeticiones(data);
        } catch (error) {
            console.error('Error al obtener peticiones:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron cargar las peticiones',
                life: 3000
            });
        }
    };

    const handleBuscarPeticion = () => {
        obtenerPeticiones();
        setMostrarTablaConsultaPeticiones(true);
    };

    const cerrarTablaConsultaPeticiones = () => {
        setMostrarTablaConsultaPeticiones(false);
    };

    // Filtrar por término de búsqueda
    useEffect(() => {
        if (!searchTermPeticion) {
            setFilteredPeticiones(peticionesConsulta);
            return;
        }
        const searchLower = searchTermPeticion.toLowerCase();
        const filtered = peticionesConsulta.filter(peticion =>
            (peticion.folio?.toString() || '').toLowerCase().includes(searchLower) ||
            (peticion.titulo || '').toLowerCase().includes(searchLower) ||
            (peticion.estatus || '').toLowerCase().includes(searchLower)
        );
        setFilteredPeticiones(filtered);
    }, [searchTermPeticion, peticionesConsulta]);

    // Seleccionar una petición
    const handleSeleccionarPeticion = (peticion: PeticionType) => {
        setFolio(peticion.folio.toString());
        setTitulo(peticion.titulo);
        setJustificacion(peticion.justificacion);
        const sucursalEncontrada = sucursales.find(s => s.nombre === peticion.sucursal);
        setSucursal(sucursalEncontrada || null);
        const departamentoEncontrado = departamentos.find(d => d.nombre === peticion.departamento);
        setDepartamento(departamentoEncontrado || null);
        setMode('view');
        cerrarTablaConsultaPeticiones();
    };

    // Limpiar formulario
    const handleLimpiar = async () => {
        setTitulo('');
        setJustificacion('');
        setSucursal(null);
        setDepartamento(null);
        setImagenSeleccionada(null);
        setPreviewImagen(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        establecerEmpleadoLoggeado();
        await generarSiguienteFolio();
        setMode('new');
    };

    const limpiarFormulario = async () => {
        setTitulo('');
        setJustificacion('');
        setSucursal(null);
        setDepartamento(null);
        setImagenSeleccionada(null);
        setPreviewImagen(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        establecerEmpleadoLoggeado();
        await generarSiguienteFolio();
    };

    const handleNuevo = async () => {
        await limpiarFormulario();
        setMode('new');
    };

    const handleEliminar = () => {
        if (!folio) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione una petición para eliminar',
                life: 2000
            });
            return;
        }
        const confirmacion = window.confirm(`¿Está seguro de eliminar la petición con folio: ${folio}?`);
        if (confirmacion) {
            fetch(API_URLS.peticiones.eliminar(folio), {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            })
                .then(response => {
                    if (!response.ok) throw new Error('Error al eliminar la petición');
                    return response.json();
                })
                .then(async () => {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Éxito',
                        detail: 'Petición eliminada correctamente',
                        life: 2000
                    });
                    await limpiarFormulario();
                })
                .catch(error => {
                    console.error('Error al eliminar:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo eliminar la petición',
                        life: 3000
                    });
                });
        }
    };

    const handleEnviar = async () => {
        if (!empleado || !titulo || !justificacion || !sucursal || !departamento) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Todos los campos con * son obligatorios',
                life: 2000
            });
            return;
        }
        const formData = new FormData();
        formData.append('cveemp', empleado.cveemp);
        formData.append('titulo', titulo);
        formData.append('justificacion', justificacion);
        formData.append('sucursal', sucursal.nombre);
        formData.append('departamento', departamento.nombre);
        formData.append('fechapromesa', '');
        formData.append('motivo', '');
        if (imagenSeleccionada) {
            formData.append('imagen', imagenSeleccionada);
        }

        try {
            const response = await fetch(API_URLS.peticiones.registrar, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Error del servidor: ${text}`);
            }
            const result = await response.json();
            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: imagenSeleccionada 
                    ? 'Petición enviada exitosamente con imagen adjunta' 
                    : 'Petición enviada exitosamente',
                life: 3000
            });
            await limpiarFormulario();
            setMode('new');
        } catch (error) {
            console.error('Error al enviar:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error instanceof Error ? error.message : 'No se pudo enviar la petición',
                life: 3000
            });
        }
    };

    // Formatear fecha
    const formatearFecha = (fecha: string) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // Color del estatus
    const getEstatusColor = (estatus: string) => {
        switch (estatus?.toUpperCase()) {
            case 'PENDIENTE': return '#ffc107';
            case 'TRABAJANDO': return '#28a745';
            case 'CANCELADO': return '#dc3545';
            case 'LISTO': return '#17a2b8';
            default: return '#6c757d';
        }
    };

    // Templates de DataTable
    const estatusBodyTemplate = (rowData: PeticionType) => (
        <div style={{ textAlign: 'center' }}>
            <Badge 
                value={rowData.estatus} 
                style={{ 
                    backgroundColor: getEstatusColor(rowData.estatus),
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                }}
            />
        </div>
    );

    const folioBodyTemplate = (rowData: PeticionType) => (
        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#3b82f6' }}>
            {rowData.folio}
        </div>
    );

    const fechaBodyTemplate = (rowData: PeticionType) => (
        <div style={{ textAlign: 'center', fontSize: '0.85rem' }}>
            {formatearFecha(rowData.fecha_hora)}
        </div>
    );

    const tituloBodyTemplate = (rowData: PeticionType) => (
        <div 
            style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={rowData.titulo}
        >
            {rowData.titulo}
        </div>
    );

    const imagenBodyTemplate = (rowData: PeticionType) => (
        <div style={{ textAlign: 'center' }}>
            {rowData.imagen_url ? (
                <i className="pi pi-image" style={{ color: '#10b981', fontSize: '1.2rem' }} title="Tiene imagen"></i>
            ) : (
                <i className="pi pi-minus" style={{ color: '#6b7280', fontSize: '1rem' }} title="Sin imagen"></i>
            )}
        </div>
    );

    return (
        <>
            {/* MODAL - Cámara */}
            {mostrarCamara && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '600px',
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>📸 Capturar Foto</h3>
                        <video ref={videoRef} autoPlay playsInline style={{
                            width: '100%',
                            maxWidth: '400px',
                            height: 'auto',
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }} />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <Button label="📸 Tomar Foto" icon="pi pi-camera" severity="success" onClick={tomarFoto} style={{ borderRadius: '8px' }} />
                            <Button label="❌ Cancelar" icon="pi pi-times" severity="secondary" onClick={cerrarCamara} style={{ borderRadius: '8px' }} />
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Tabla de Consulta */}
            {mostrarTablaConsultaPeticiones && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        width: '90%',
                        maxWidth: '1200px',
                        height: '80%',
                        maxHeight: '700px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
                                📋 Mis Peticiones
                            </h2>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <InputText
                                placeholder="🔍 Buscar por folio, título o estatus..."
                                value={searchTermPeticion}
                                onChange={(e) => setSearchTermPeticion(e.target.value)}
                                style={{ width: '100%', height: '44px', paddingLeft: '40px', border: '2px solid #e5e7eb', borderRadius: '12px' }}
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <DataTable
                                value={filteredPeticiones}
                                scrollable
                                scrollHeight="100%"
                                emptyMessage="📭 No tienes peticiones registradas"
                                selectionMode="single"
                               
                            >
                                <Column field="folio" header="📄 Folio" sortable body={folioBodyTemplate} style={{ width: '80px' }} />
                                <Column field="fecha_hora" header="📅 Fecha" sortable body={fechaBodyTemplate} style={{ width: '100px' }} />
                                <Column field="titulo" header="📝 Título" sortable body={tituloBodyTemplate} style={{ minWidth: '200px' }} />
                                <Column field="estatus" header="🏷️ Estatus" sortable body={estatusBodyTemplate} style={{ width: '120px' }} />
                                <Column field="imagen_url" header="🖼️ Imagen" body={imagenBodyTemplate} style={{ width: '80px' }} />
                            </DataTable>
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <Button label="Cerrar" icon="pi pi-times" severity="secondary" onClick={cerrarTablaConsultaPeticiones} />
                        </div>
                    </div>
                </div>
            )}

            {/* Tour Guiado */}
            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous={true}
                showProgress={true}
                showSkipButton={true}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        zIndex: 10000,
                        primaryColor: '#3b82f6',
                        textColor: '#1f2937',
                        backgroundColor: '#ffffff',
                        arrowColor: '#ffffff',
                    },
                    tooltip: {
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    },
                }}
            />

            {/* Renderizado principal */}
            <Sidebar />
            <Navbar />
            <Toast ref={toast} />

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                padding: '20px'
            }}>
                {/* Botón para iniciar el tour */}
                {!tourCompleted && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        zIndex: 1001
                    }}>
                        <Button
                            label="🎯 Iniciar Tour"
                            icon="pi pi-info-circle"
                            severity="help"
                            raised
                            onClick={() => setRunTour(true)}
                            style={{
                                borderRadius: '8px',
                                fontWeight: '600',
                                fontSize: '1rem'
                            }}
                        />
                    </div>
                )}

                {/* Resto del contenido */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700' }}>
                            📝 Nueva Petición
                        </h1>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #10b981, #3b82f6)', borderRadius: '1px' }}></div>
                </div>

                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontWeight: '600' }}>
                        💡 Complete todos los campos marcados con (*) para enviar su petición
                    </div>
                    <Button
                        label="🏠 Ir al Inicio"
                        icon="pi pi-home"
                        severity="info"
                        outlined
                        onClick={handleIrAlInicio}
                        data-tour="ver-peticiones"
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    />
                </div>

                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '32px'
                }}>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    📄 Folio
                                </label>
                                <InputText 
                                    value={folio} 
                                    disabled 
                                    placeholder="Generando folio..."
                                    data-tour="folio"
                                    style={{ width: '100%', height: '44px', backgroundColor: '#f3f4f6', fontWeight: 'bold', borderRadius: '8px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    👤 Empleado<span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText 
                                    value={empleado?.nomemp || 'No hay empleado loggeado'} 
                                    disabled 
                                    placeholder="Empleado loggeado"
                                    data-tour="empleado"
                                    style={{ width: '100%', height: '44px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🏢 Sucursal<span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Dropdown 
                                    value={sucursal} 
                                    options={sucursales}
                                    optionLabel="nombre"
                                    onChange={(e) => setSucursal(e.value)}
                                    required 
                                    placeholder="Seleccione una sucursal"
                                    data-tour="sucursal"
                                    style={{ width: '100%', height: '44px', borderRadius: '8px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🏬 Departamento<span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Dropdown 
                                    value={departamento} 
                                    options={departamentos}
                                    optionLabel="nombre"
                                    onChange={(e) => setDepartamento(e.value)}
                                    required 
                                    placeholder="Seleccione un departamento"
                                    data-tour="departamento"
                                    style={{ width: '100%', height: '44px', borderRadius: '8px' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                📝 Título<span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <InputText 
                                value={titulo} 
                                onChange={(e) => setTitulo(e.target.value)}
                                required 
                                placeholder="Ingrese el título de la petición"
                                data-tour="titulo"
                                style={{ width: '100%', height: '44px', borderRadius: '8px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                📋 Justificación<span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <InputTextarea 
                                value={justificacion} 
                                onChange={(e) => setJustificacion(e.target.value)}
                                rows={5} 
                                required
                                placeholder="Describa detalladamente la justificación de su petición"
                                data-tour="justificacion"
                                style={{ width: '100%', borderRadius: '8px', minHeight: '120px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '32px' }} data-tour="imagen">
                            <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                📷 Imagen (Opcional)
                            </label>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                <Button label="📁 Seleccionar Archivo" icon="pi pi-upload" severity="info" outlined onClick={() => fileInputRef.current?.click()} style={{ borderRadius: '8px' }} />
                                <Button label="📸 Tomar Foto" icon="pi pi-camera" severity="success" outlined onClick={iniciarCamara} style={{ borderRadius: '8px' }} />
                                {imagenSeleccionada && (
                                    <Button label="🗑️ Eliminar Imagen" icon="pi pi-trash" severity="danger" outlined onClick={eliminarImagen} style={{ borderRadius: '8px' }} />
                                )}
                            </div>
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleSeleccionarArchivo} style={{ display: 'none' }} />
                            {previewImagen && (
                                <div style={{
                                    border: '2px dashed #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    textAlign: 'center',
                                    backgroundColor: '#f9fafb'
                                }}>
                                    <img src={previewImagen} alt="Preview" style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }} />
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', paddingTop: '24px', borderTop: '2px solid #f3f4f6' }} data-tour="acciones">
                            <Button 
                                label={imagenSeleccionada ? "📤 Enviar con Imagen" : "📤 Enviar Petición"}
                                icon="pi pi-send" 
                                severity="success"
                                onClick={handleEnviar}
                                style={{ fontSize: '1rem', padding: '14px 28px', fontWeight: '700', borderRadius: '10px' }}
                            />
                            <Button 
                                label="🗑️ Limpiar Datos" 
                                icon="pi pi-refresh" 
                                severity="warning"
                                outlined
                                onClick={handleLimpiar}
                                style={{ fontSize: '1rem', padding: '14px 28px', fontWeight: '600', borderRadius: '10px' }}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Peticiones;